/**
 * Button — the primary action primitive.
 *
 * Anatomy (design system §6 "Content primitives"): h-8 (32px), `rounded-[3px]`,
 * `px-150`, label at `font-[570]` (the Inter weight bump), optional 16px leading
 * icon. `.c9-touch` grows it to 44px on coarse-pointer devices.
 */
import { Show, children, splitProps, type JSX, type ParentProps } from "solid-js";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";
import { Spinner } from "./spinner";

export type ButtonAppearance = "primary" | "default" | "subtle" | "danger";
export type ButtonSize = "default" | "compact";

const APPEARANCE: Record<ButtonAppearance, string> = {
  primary:
    "bg-[var(--c9-bg-brand-bold)] text-[color:var(--c9-text-inverse)] " +
    "hover:bg-[var(--c9-bg-brand-bold-hovered)] active:bg-[var(--c9-bg-brand-bold-pressed)]",
  default:
    "bg-[var(--c9-bg-neutral)] text-[color:var(--c9-text)] " +
    "hover:bg-[var(--c9-bg-neutral-hovered)] active:bg-[var(--c9-bg-neutral-pressed)]",
  subtle:
    "bg-transparent text-[color:var(--c9-text-subtle)] " +
    "hover:bg-[var(--c9-bg-neutral-subtle-hovered)] active:bg-[var(--c9-bg-neutral-subtle-pressed)]",
  danger:
    "bg-[var(--c9-bg-danger-bold)] text-[color:var(--c9-text-inverse)] " +
    "hover:bg-[var(--c9-bg-danger-bold-hovered)] active:bg-[var(--c9-bg-danger-bold-pressed)]",
};

export interface ButtonProps
  extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  /** 16px leading icon. */
  iconBefore?: JSX.Element;
  iconAfter?: JSX.Element;
  /** Shows a spinner and blocks interaction. */
  loading?: boolean;
  /** Stretch to the container width. */
  full?: boolean;
}

export function Button(props: ParentProps<ButtonProps>): JSX.Element {
  const [local, rest] = splitProps(props, [
    "appearance",
    "size",
    "iconBefore",
    "iconAfter",
    "loading",
    "full",
    "class",
    "children",
    "disabled",
  ]);

  // JSX.Element props: resolve once (reading them twice builds two trees).
  const before = children(() => local.iconBefore);
  const after = children(() => local.iconAfter);

  const appearance = () => local.appearance ?? "default";
  const isDisabled = () => local.disabled || local.loading;

  return (
    <button
      type="button"
      {...rest}
      disabled={isDisabled()}
      aria-busy={local.loading ? "true" : undefined}
      class={cn(
        "c9-touch inline-flex shrink-0 items-center justify-center gap-075 whitespace-nowrap",
        "rounded-[3px] text-body font-[570] transition-colors",
        local.size === "compact" ? "h-6 px-100" : "h-8 px-150",
        local.full && "w-full",
        APPEARANCE[appearance()],
        "disabled:cursor-not-allowed disabled:bg-[var(--c9-bg-disabled)] disabled:text-[color:var(--c9-text-disabled)]",
        focusRing,
        local.class,
      )}
    >
      <Show when={local.loading}>
        <Spinner size="xsmall" />
      </Show>
      <Show when={!local.loading}>{before()}</Show>
      {local.children}
      {after()}
    </button>
  );
}
